import { z } from 'zod';

export const IdentityRole = z.enum(['super_admin', 'namespace_admin', 'readonly']);
export type IdentityRole = z.infer<typeof IdentityRole>;

export const IdentityStatus = z.enum(['active', 'disabled', 'pending', 'locked']);
export type IdentityStatus = z.infer<typeof IdentityStatus>;

// 'sa' is the service-account auth source the backend emits for SA accounts.
export const AuthSource = z.enum(['local', 'keycloak', 'oidc', 'sa']);
export type AuthSource = z.infer<typeof AuthSource>;

export const Identity = z.object({
  id: z.string(),
  username: z.string().optional(),
  email: z.string(),
  displayName: z.string().optional(),
  role: IdentityRole,
  status: IdentityStatus,
  authSource: AuthSource.default('local'),
  groups: z.array(z.string()).optional(),
  language: z.string().optional(),
  mfaEnabled: z.boolean().optional(),
  mfaRequired: z.boolean().optional(),
  createdAt: z.string().optional(),
  lastLoginAt: z.string().nullable().optional(),
  namespaces: z.array(z.string()).optional(),
});
export type Identity = z.infer<typeof Identity>;

export const Namespace = z.object({
  name: z.string(),
  labels: z.record(z.string(), z.string()).optional(),
  users: z.number().optional(),
  bindings: z.number().optional(),
  lastChange: z.string().optional(),
  status: z.string().optional(),
});
export type Namespace = z.infer<typeof Namespace>;

export const Role = z.object({
  name: z.string(),
  scope: z.enum(['namespaced', 'cluster']).optional(),
  description: z.string().optional(),
  origin: z.enum(['builtin', 'custom']).optional(),
});
export type Role = z.infer<typeof Role>;

export const Binding = z.object({
  id: z.string(),
  identityId: z.string().optional(),
  namespace: z.string().optional(),
  // Wire field is role_name → roleName after client normalization.
  roleName: z.string(),
  roleKind: z.string().optional(),
  scope: z.enum(['namespaced', 'cluster']).default('namespaced'),
  grantedBy: z.string().optional(),
  grantedAt: z.string().optional(),
  expiresAt: z.string().nullable().optional(),
});
export type Binding = z.infer<typeof Binding>;

export const AuditEvent = z.object({
  id: z.string(),
  timestamp: z.string(),
  actor: z.string(),
  actorIp: z.string().optional(),
  action: z.string(),
  category: z.string().optional(),
  target: z.string().optional(),
  targetKind: z.string().optional(),
  namespace: z.string().optional(),
  result: z.enum(['success', 'denied', 'error']).default('success'),
  // On the wire `details` is a JSON string; some mocks pass an object — accept both.
  details: z.unknown().optional(),
});
export type AuditEvent = z.infer<typeof AuditEvent>;

export const GroupRoleMapping = z.object({
  id: z.string().optional(),
  group: z.string(),
  role: IdentityRole,
  namespaces: z.array(z.string()).optional(),
  priority: z.number().default(0),
});
export type GroupRoleMapping = z.infer<typeof GroupRoleMapping>;

export const IdpConfig = z.object({
  enabled: z.boolean().default(false),
  type: z.literal('keycloak').default('keycloak'),
  issuerUrl: z.string().default(''),
  clientId: z.string().default(''),
  clientSecretSet: z.boolean().optional(),
  scopes: z.string().default('openid profile email groups'),
  groupsClaim: z.string().default('groups'),
  redirectUri: z.string().optional(),
  backchannelLogoutUri: z.string().optional(),
  autoProvision: z.boolean().default(true),
  requireVerifiedEmail: z.boolean().default(true),
  disableLocalForFederated: z.boolean().default(false),
  backchannelLogoutEnabled: z.boolean().default(true),
  mappings: z.array(GroupRoleMapping).default([]),
  lastTestedAt: z.string().optional(),
  discoveryStatus: z.enum(['ok', 'failed', 'unknown']).optional(),
});
export type IdpConfig = z.infer<typeof IdpConfig>;

export const Paginated = <T extends z.ZodTypeAny>(item: T) =>
  z.object({
    items: z.array(item),
    total: z.number().optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  });
export type Paginated<T> = { items: T[]; total?: number; page?: number; pageSize?: number };

export const LoginResponse = z.object({
  status: z.enum(['authenticated', 'mfa_required', 'mfa_enrollment_required']),
  mfaToken: z.string().optional(),
  enrollToken: z.string().optional(),
  user: Identity.optional(),
});
export type LoginResponse = z.infer<typeof LoginResponse>;

export const DashboardStats = z.object({
  users: z.object({ total: z.number(), active: z.number(), disabled: z.number() }),
  audit: z.object({
    total: z.number(),
    windowDays: z.number(),
    windowTotal: z.number(),
    histogram: z.array(z.object({ day: z.string(), count: z.number() })),
  }),
});
export type DashboardStats = z.infer<typeof DashboardStats>;

export const KubeconfigResponse = z.object({
  kubeconfig: z.string(),
  expiresAt: z.string().nullable().optional(),
  // Identifies the credential in the token list, so it can be revoked later.
  tokenId: z.string().nullable().optional(),
  // The names the server actually applied — it may have filled in defaults for
  // the fields the form left empty.
  clusterName: z.string().nullable().optional(),
  userName: z.string().nullable().optional(),
  contextName: z.string().nullable().optional(),
});
export type KubeconfigResponse = z.infer<typeof KubeconfigResponse>;

// One kubeconfig handed out to a user. Never carries the token itself: that is
// shown once, at download time.
export const IssuedToken = z.object({
  id: z.string(),
  status: z.enum(['active', 'expired', 'revoked']),
  issuedAt: z.string(),
  expiresAt: z.string(),
  issuedByEmail: z.string().nullable().optional(),
  revokedAt: z.string().nullable().optional(),
  revokedByEmail: z.string().nullable().optional(),
});
export type IssuedToken = z.infer<typeof IssuedToken>;

export const RevokeResult = z.object({
  ok: z.boolean(),
  revoked: z.number(),
  // Seconds the API server may still accept the token: it caches successful
  // validations, so revocation is not instantaneous.
  effectiveInSeconds: z.number(),
});
export type RevokeResult = z.infer<typeof RevokeResult>;

// Instance-wide settings, editable by a super_admin in Settings.
export const AppSettings = z.object({
  // Labels the cluster in every kubeconfig this instance hands out.
  clusterName: z.string(),
});
export type AppSettings = z.infer<typeof AppSettings>;

export const IdpTestResult = z.object({
  ok: z.boolean(),
  issuer: z.string().optional(),
  authorizationEndpoint: z.string().optional(),
  tokenEndpoint: z.string().optional(),
  jwksUri: z.string().optional(),
  keyCount: z.number().optional(),
  error: z.string().optional(),
});
export type IdpTestResult = z.infer<typeof IdpTestResult>;
